import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { Patient } from '../store/slices/patientsSlice';
import {
  Badge,
  Card,
  Input,
  Button,
  CardContent,
  Checkbox,
} from '@roger/ui-components';
import PatientSnapshotDialog from '../components/PatientSnapshotDialog';
import { ImportedPatientBadge } from '../components/patient/ImportedPatientBadge';
import { TestPatientBadge } from '../components/patient/TestPatientBadge';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../hooks/useAccessibleClinics';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { useDebounce } from '../hooks/useDebounce';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const formatCityState = (patient: Patient) => {
  const parts: string[] = [];
  if (patient.city) parts.push(patient.city);
  if (patient.state) parts.push(patient.state);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const Patients: React.FC = () => {
  const navigate = useNavigate();
  const {
    patients,
    isPatientsLoading: loading,
    error,
    fetchPatients,
    searchTerm,
    setSearchTerm,
    showInactive,
    setShowInactive,
    hideTest,
    setHideTest,
    patientManagerHideImportedPatients,
    setPatientManagerHideImportedPatients,
    alphaCounts,
    currentPage,
    pageSize,
    totalPatients,
    surnameInitial,
    setCurrentPage,
    setSurnameInitial,
  } = useStore(
    useShallow(state => ({
      patients: state.patients,
      isPatientsLoading: state.isPatientsLoading,
      error: state.error,
      fetchPatients: state.fetchPatients,
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      showInactive: state.showInactive,
      setShowInactive: state.setShowInactive,
      hideTest: state.hideTest,
      setHideTest: state.setHideTest,
      patientManagerHideImportedPatients:
        state.patientManagerHideImportedPatients,
      setPatientManagerHideImportedPatients:
        state.setPatientManagerHideImportedPatients,
      alphaCounts: state.alphaCounts,
      currentPage: state.currentPage,
      pageSize: state.pageSize,
      totalPatients: state.totalPatients,
      surnameInitial: state.surnameInitial,
      setCurrentPage: state.setCurrentPage,
      setSurnameInitial: state.setSurnameInitial,
    }))
  );

  const {
    clinics: clinicOptions,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
    shouldShowPicker,
  } = useAccessibleClinics({ persistKey: 'patientList' });

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [snapshotPatient, setSnapshotPatient] = useState<Patient | null>(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const safePatients = patients || [];
  const totalPages = Math.max(1, Math.ceil(totalPatients / pageSize));
  const firstShown = totalPatients === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastShown = Math.min(currentPage * pageSize, totalPatients);

  const debouncedSearch = useDebounce((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, 350);

  // Keep matching, totals, and A-Z availability on the bounded server query.
  useEffect(() => {
    void fetchPatients({
      page: currentPage,
      search: searchTerm,
      clinic_id: isAllClinics ? null : selectedClinicId,
      surname_initial: surnameInitial || null,
      show_inactive: showInactive,
      hide_test: hideTest,
      hide_imported_patients: patientManagerHideImportedPatients,
    });
  }, [
    currentPage,
    fetchPatients,
    hideTest,
    isAllClinics,
    patientManagerHideImportedPatients,
    searchTerm,
    selectedClinicId,
    showInactive,
    surnameInitial,
  ]);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <div className='flex items-center gap-2'>
          <h1 className='text-2xl font-bold'>Patient Manager</h1>
          {!loading && !error && (
            <Badge variant='secondary' data-testid='patient-count-badge'>
              {totalPatients} {totalPatients === 1 ? 'patient' : 'patients'}
            </Badge>
          )}
        </div>
        <p className='text-muted-foreground text-sm'>
          Comprehensive patient management with AI-powered insights and
          analytics
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertTriangle className='h-4 w-4' />
              <span className='font-medium'>Error loading patients</span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{error}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => fetchPatients()}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Patient List Controls */}
      <Card>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
          <Input
            placeholder='Search all agency patients - Name, MRN...'
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              setSearchInput(value);
              debouncedSearch(value);
            }}
            className='flex-1'
          />
          {shouldShowPicker && (
            <ClinicSelect
              className='w-full sm:w-56 shrink-0'
              clinics={clinicOptions}
              includeAllOption
              allOptionValue={ALL_CLINICS}
              value={selectedClinicId}
              onChange={e => {
                setSelectedClinicId(e.target.value);
                setCurrentPage(1);
              }}
              placeholder='Filter by clinic'
            />
          )}
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4'>
            <div className='flex items-center gap-2 shrink-0'>
              <Checkbox
                id='showInactive'
                checked={showInactive}
                onCheckedChange={(checked: boolean) => {
                  setShowInactive(!!checked);
                  setCurrentPage(1);
                }}
              />
              <label
                htmlFor='showInactive'
                className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Show inactive patients
              </label>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              <Checkbox
                id='hideTest'
                data-testid='hide-test-toggle'
                checked={hideTest}
                onCheckedChange={(checked: boolean) => {
                  setHideTest(!!checked);
                  setCurrentPage(1);
                }}
              />
              <label
                htmlFor='hideTest'
                className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Hide test patients
              </label>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              <Checkbox
                id='hideImportedPatients'
                checked={patientManagerHideImportedPatients}
                onCheckedChange={(checked: boolean) => {
                  setPatientManagerHideImportedPatients(!!checked);
                  setCurrentPage(1);
                }}
              />
              <label
                htmlFor='hideImportedPatients'
                className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Filter out imported patients
              </label>
            </div>
          </div>
        </div>
        {/* Alphabetical Navigation */}
        <div className='mt-4 flex flex-wrap gap-1'>
          {alphabet.map(letter => (
            <Button
              key={letter}
              variant={surnameInitial === letter ? 'default' : 'outline'}
              size='sm'
              className='px-2 py-1 text-xs'
              onClick={() => {
                setSurnameInitial(surnameInitial === letter ? '' : letter);
                setCurrentPage(1);
              }}
              disabled={!alphaCounts[letter] && surnameInitial !== letter}
            >
              {letter}
            </Button>
          ))}
        </div>
      </Card>

      {/* Patient Cards List */}
      <Card>
        {!loading && (
          <div className='mb-2 text-xs text-muted-foreground'>
            Showing {firstShown}-{lastShown} of {totalPatients} patients
          </div>
        )}
        <div className='flex flex-wrap gap-3'>
          {loading ? (
            <div className='text-xs text-muted-foreground'>
              Loading patients...
            </div>
          ) : safePatients.length === 0 ? (
            <div className='text-xs text-muted-foreground'>
              No patients found.
            </div>
          ) : (
            safePatients.map(patient => {
              try {
                return (
                  <Card
                    key={patient.id}
                    className='p-3 sm:p-3 md:p-3 w-full sm:w-64 min-w-0 cursor-pointer hover:shadow-sm transition-shadow relative'
                    onClick={e => {
                      // Only navigate if not clicking Eye icon
                      if ((e.target as HTMLElement).closest('.eye-btn')) return;
                      navigate(`/patients/${patient.id}`);
                    }}
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-1.5'>
                        <div className='font-medium truncate text-sm'>
                          {patient.name || 'Unknown Patient'}
                        </div>
                        {patient.is_test_patient && (
                          <TestPatientBadge className='shrink-0' />
                        )}
                        {patient.import_source &&
                          !patient.import_badge_hidden_at && (
                            <ImportedPatientBadge
                              importedAt={patient.imported_at}
                              className='shrink-0'
                            />
                          )}
                      </div>
                      <div className='text-xs text-muted-foreground space-y-0.5 mt-1'>
                        <div className='truncate'>
                          MRN: {patient.mrn || 'N/A'}
                        </div>
                        <div className='truncate'>
                          DOB:{' '}
                          {formatDateToMMDDYYYY(patient.date_of_birth) || 'N/A'}
                        </div>
                        <div className='truncate'>
                          {patient.address || 'N/A'}
                          {(patient.city || patient.state) && ', '}
                          {formatCityState(patient)}
                        </div>
                        <div className='truncate'>
                          Insurance: {patient.insurance_name || 'N/A'}
                        </div>
                        <div className='truncate'>
                          Clinic: {patient.clinic_name || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {/* Eye Icon Button */}
                    <button
                      className='eye-btn absolute top-2 right-2 p-1 rounded hover:bg-muted'
                      onClick={e => {
                        e.stopPropagation();
                        setSnapshotPatient(patient);
                        setSnapshotOpen(true);
                      }}
                      aria-label='View Snapshot'
                      type='button'
                    >
                      <Eye className='w-5 h-5 text-muted-foreground' />
                    </button>
                  </Card>
                );
              } catch (error) {
                console.error('Error rendering patient card:', error, patient);
                return (
                  <Card
                    key={patient.id || 'error'}
                    className='p-3 sm:p-3 md:p-3 w-full sm:w-64 min-w-0'
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='font-medium truncate text-sm text-destructive'>
                        Error Loading Patient
                      </div>
                      <div className='text-xs text-muted-foreground truncate'>
                        ID: {patient.id || 'Unknown'}
                      </div>
                    </div>
                  </Card>
                );
              }
            })
          )}
        </div>
        {!loading && totalPatients > 0 && (
          <div className='mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between'>
            <span className='text-muted-foreground'>
              Page {currentPage} of {totalPages}
            </span>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Previous
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
      {/* Patient Snapshot Dialog */}
      <PatientSnapshotDialog
        patient={snapshotPatient as Patient}
        open={snapshotOpen}
        onClose={() => setSnapshotOpen(false)}
      />
    </div>
  );
};

export default Patients;
